import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaInstagram, FaFacebook, FaWhatsapp, FaHeart, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import CompanyInfoContext from '../context/CompanyInfoContext';

const Footer = () => {
  const { companyName, companyLogo } = useContext(CompanyInfoContext);
  const [contactDetails, setContactDetails] = useState({
    phone: '',
    email: '',
    instagram: '',
    facebook: '',
    whatsapp: ''
  });

  useEffect(() => {
    fetchContactDetails();
  }, []);

  const fetchContactDetails = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/contactDetails`);
      if (res.ok) {
        const data = await res.json();
        if (data) {
          setContactDetails(data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch contact details');
    }
  };

  return (
    <footer className="relative bg-slate-900 dark:bg-dark-bg text-white overflow-hidden">
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 bg-pattern-crochet opacity-30"></div>

      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-600 via-teal-400 to-teal-600"></div>

      <div className="relative container mx-auto px-4 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-10">
          {/* Brand Section */}
          <div className="lg:col-span-1 space-y-5">
            <Link to="/" className="flex items-center space-x-3 group">
              {companyLogo ? (
                <img
                  src={`${process.env.REACT_APP_API_URL}/api/images/${companyLogo}?w=200`}
                  alt="Logo"
                  loading="lazy"
                  className="h-12 w-12 object-contain rounded-xl bg-white/10 p-1"
                />
              ) : (
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
              )}
              <div>
                <span className="font-display font-bold text-xl text-white">{companyName}</span>
                <span className="block text-[10px] uppercase tracking-[0.15em] text-teal-400 font-semibold">Handcrafted Artistry</span>
              </div>
            </Link>
            <p className="text-slate-400 leading-relaxed text-sm">
              Creating unique handmade crochet products with passion and dedication. Each piece is crafted with love and premium materials.
            </p>

            {/* Social Media Links */}
            <div className="flex space-x-3 pt-2">
              {contactDetails.instagram && (
                <a
                  href={contactDetails.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 dark:bg-dark-elevated hover:bg-teal-600 rounded-xl flex items-center justify-center transition-all duration-200"
                  title="Instagram"
                >
                  <FaInstagram className="text-lg" />
                </a>
              )}
              {contactDetails.facebook && (
                <a
                  href={contactDetails.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 dark:bg-dark-elevated hover:bg-teal-600 rounded-xl flex items-center justify-center transition-all duration-200"
                  title="Facebook"
                >
                  <FaFacebook className="text-lg" />
                </a>
              )}
              {contactDetails.whatsapp && (
                <a
                  href={contactDetails.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 dark:bg-dark-elevated hover:bg-emerald-600 rounded-xl flex items-center justify-center transition-all duration-200"
                  title="WhatsApp"
                >
                  <FaWhatsapp className="text-lg" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-display font-semibold text-lg mb-5 text-white">Quick Links</h3>
            <ul className="space-y-3">
              {[
                { to: '/', label: 'Home' },
                { to: '/products', label: 'Shop' },
                { to: '/testimonials', label: 'Reviews' },
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact' },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-slate-400 hover:text-teal-400 transition-colors duration-200 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h3 className="font-display font-semibold text-lg mb-5 text-white">Customer Service</h3>
            <ul className="space-y-3">
              {[
                { to: '/contact', label: 'Contact Us' },
                { to: '/orders', label: 'My Orders' },
                { to: '/wishlist', label: 'Wishlist' },
                { to: '/account', label: 'My Account' },
                { to: '/cart', label: 'Shopping Cart' },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-slate-400 hover:text-teal-400 transition-colors duration-200 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-display font-semibold text-lg mb-5 text-white">Get in Touch</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <div className="w-9 h-9 bg-slate-800 dark:bg-dark-elevated rounded-lg flex items-center justify-center flex-shrink-0">
                  <FaEnvelope className="text-teal-400" />
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Email</span>
                  <p className="text-slate-300 text-sm">{contactDetails.email || 'hello@example.com'}</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-9 h-9 bg-slate-800 dark:bg-dark-elevated rounded-lg flex items-center justify-center flex-shrink-0">
                  <FaPhone className="text-teal-400" />
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Phone</span>
                  <p className="text-slate-300 text-sm">{contactDetails.phone || '+91 XXXXX XXXXX'}</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-9 h-9 bg-slate-800 dark:bg-dark-elevated rounded-lg flex items-center justify-center flex-shrink-0">
                  <FaMapMarkerAlt className="text-teal-400" />
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Location</span>
                  <p className="text-slate-300 text-sm">India</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 dark:border-dark-border pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm text-center md:text-left">
              &copy; {new Date().getFullYear()} {companyName}. All rights reserved.
            </p>
            <p className="text-slate-500 text-sm flex items-center gap-2">
              Crafted with <FaHeart className="text-teal-500" /> for handmade lovers
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
